// Drop-in replacement for twilioVoiceClient when running against a tvapi
// (Twilio-compatible) server with FreeSWITCH for media. Uses JsSIP to
// register a UA over WebSocket and place an INVITE to the conference URI
// exposed by tvapi's FS dialplan.
//
// Exposes the same public API as TwilioVoiceClient:
//   - initializeDevice(inboxId)
//   - joinClientCall({ to, conversationId })
//   - endClientCall()
//   - destroyDevice()
//   - hasActiveConnection (getter)
// and dispatches a 'call:disconnected' CustomEvent when the session ends,
// so useCallSession.js and stores/calls.js keep working unchanged.

import JsSIP from 'jssip';
import VoiceAPI from './voiceAPIClient';

// tvapi FS dialplan matches `conf-<X>` where X is the Chatwoot conference
// friendly name (e.g. "conf_account_7_conv_16" from Voice::Conference::Name).
const CONFERENCE_USER_PREFIX = 'conf-';

const createCallDisconnectedEvent = () => new CustomEvent('call:disconnected');

class JsSipVoiceClient extends EventTarget {
  constructor() {
    super();
    this.ua = null;
    this.session = null;
    this.initialized = false;
    this.inboxId = null;
    this.realm = null;
    this.remoteAudioEl = null;
  }

  async initializeDevice(inboxId) {
    this.destroyDevice();

    const creds = await VoiceAPI.getSipCredentials(inboxId);
    if (!creds?.ws_url || !creds?.username || !creds?.password) {
      throw new Error('Invalid SIP credentials');
    }

    const socket = new JsSIP.WebSocketInterface(creds.ws_url);
    this.ua = new JsSIP.UA({
      sockets: [socket],
      uri: `sip:${creds.username}@${creds.realm}`,
      password: creds.password,
      register: true,
      session_timers: false,
    });

    this.realm = creds.realm;

    this.ua.on('newRTCSession', event => this.handleNewSession(event));
    this.ua.start();

    this.initialized = true;
    this.inboxId = inboxId;
    return this.ua;
  }

  handleNewSession({ session }) {
    this.session = session;
    session.on('ended', this.onDisconnect);
    session.on('failed', this.onDisconnect);

    // Incoming INVITEs (agent receives a call from FS conference): auto-accept
    // to mimic the Twilio Device behavior where the server-originated leg
    // connects immediately.
    if (session.direction === 'incoming') {
      session.answer({ mediaConstraints: { audio: true, video: false } });
    }

    session.connection.addEventListener('track', ({ streams }) => {
      this.attachRemoteAudio(streams[0]);
    });
  }

  attachRemoteAudio(stream) {
    if (!this.remoteAudioEl) {
      this.remoteAudioEl = document.createElement('audio');
      this.remoteAudioEl.autoplay = true;
      this.remoteAudioEl.id = 'jssip-remote-audio';
      document.body.appendChild(this.remoteAudioEl);
    }
    this.remoteAudioEl.srcObject = stream;
  }

  get hasActiveConnection() {
    return !!this.session;
  }

  async joinClientCall({ to }) {
    if (!this.ua || !this.initialized || !to) return null;
    if (this.session) return this.session;

    const target = `sip:${CONFERENCE_USER_PREFIX}${to}@${this.realm}`;
    this.ua.call(target, {
      mediaConstraints: { audio: true, video: false },
      rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
    });
    return this.session;
  }

  endClientCall() {
    if (this.session) {
      this.session.terminate();
    }
    this.session = null;
  }

  setMuted(isMuted) {
    if (!this.session) return;
    if (isMuted) {
      this.session.mute({ audio: true });
    } else {
      this.session.unmute({ audio: true });
    }
  }

  destroyDevice() {
    if (this.session) this.session.terminate();
    if (this.ua) this.ua.stop();
    if (this.remoteAudioEl) {
      this.remoteAudioEl.srcObject = null;
      this.remoteAudioEl.remove();
      this.remoteAudioEl = null;
    }
    this.session = null;
    this.ua = null;
    this.initialized = false;
    this.inboxId = null;
    this.realm = null;
  }

  onDisconnect = () => {
    this.session = null;
    this.dispatchEvent(createCallDisconnectedEvent());
  };
}

export default new JsSipVoiceClient();
